/**
 * Login screen.
 *
 * The MFA field only appears once the API says it is needed — asking every user
 * for a code they may not have is how people give up on the first screen.
 */

import { useState, type FormEvent } from 'react'

import { ErroApi } from '../api/cliente'
import { Aviso, Botao, Campo } from '../componentes/ui'
import { MarcaEgba, NOME_PLATAFORMA } from '../componentes/marca'
import { useAuth } from '../estado/auth'

export function Login() {
  const { entrar } = useAuth()

  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [codigoMfa, setCodigoMfa] = useState('')
  const [pedirMfa, setPedirMfa] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function aoEnviar(e: FormEvent) {
    e.preventDefault()
    setErro(null)
    setEnviando(true)

    try {
      await entrar(email, senha, pedirMfa ? codigoMfa : undefined)
      // O roteador reage à mudança de estado; não há navegação manual aqui.
    } catch (falha) {
      if (falha instanceof ErroApi && falha.codigo === 'mfa_necessario') {
        setPedirMfa(true)
        setErro(null)
      } else {
        setErro(falha instanceof Error ? falha.message : 'Não foi possível entrar.')
      }
    } finally {
      setEnviando(false)
    }
  }

  return (
    <main className="grid min-h-dvh bg-white lg:grid-cols-2">
      {/* ---------------------------------------------------------- Formulário */}
      <div className="flex items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-[380px]">
          <MarcaEgba variante="completo" altura={54} className="mb-8" />

          <p className="mb-2 text-[11px] font-semibold tracking-[0.14em] text-marca-700 uppercase">
            {NOME_PLATAFORMA}
          </p>
          <h1 className="text-[26px] leading-tight font-semibold tracking-tight text-slate-900">
            Entrar na plataforma
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-500">
            Acesso restrito a usuários autorizados da EGBA, parceiros e instituições
            cadastradas.
          </p>

          <form onSubmit={aoEnviar} className="mt-8 space-y-4" noValidate>
            <Campo
              rotulo="E-mail"
              type="email"
              autoComplete="username"
              required
              obrigatorio
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@instituicao.com.br"
            />

            <Campo
              rotulo="Senha"
              type="password"
              autoComplete="current-password"
              required
              obrigatorio
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
            />

            {pedirMfa && (
              <div className="surgir">
                <Campo
                  rotulo="Código de verificação"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={7}
                  required
                  obrigatorio
                  value={codigoMfa}
                  onChange={(e) => setCodigoMfa(e.target.value)}
                  placeholder="000000"
                  ajuda="Código de 6 dígitos do seu aplicativo autenticador."
                  className="text-center text-lg tracking-[0.45em]"
                />
              </div>
            )}

            {erro && <Aviso tom="perigo">{erro}</Aviso>}

            <Botao type="submit" tamanho="lg" carregando={enviando} className="w-full">
              {enviando ? 'Verificando…' : 'Entrar'}
            </Botao>
          </form>

          <p className="mt-10 text-center text-[11px] text-slate-400">
            Plataforma da EGBA — Empresa Gráfica da Bahia · desenvolvida por{' '}
            <span className="font-medium text-slate-500">Canal · Rentex</span>
          </p>
        

          <p className="mt-10 text-xs leading-relaxed text-slate-400">
            Ao entrar você concorda com os Termos de Uso e a Política de Privacidade.
            Os acessos são registrados para fins de auditoria, conforme a LGPD.
          </p>
        </div>
      </div>

      {/* ------------------------------------------------- Painel institucional */}
      <aside className="relative hidden overflow-hidden bg-marca-900 lg:block">
        <div
          className="absolute inset-0"
          aria-hidden="true"
          style={{
            backgroundImage:
              'radial-gradient(120% 90% at 15% 10%, #0c64b0 0%, transparent 55%), radial-gradient(90% 80% at 90% 85%, #072644 0%, transparent 60%)',
          }}
        />
        {/* Trama sutil de linhas: referência à composição em colunas do Diário. */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          aria-hidden="true"
          style={{
            backgroundImage:
              'repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 84px)',
          }}
        />

        <div className="relative flex h-full flex-col justify-between p-12 xl:p-16">
          <MarcaEgba variante="wordmark" altura={30} paraFundoEscuro />

          <div className="max-w-lg">
            <p className="text-[11px] font-semibold tracking-[0.18em] text-marca-300 uppercase">
              Diário Oficial do Estado da Bahia
            </p>
            <h2 className="mt-5 text-[32px] leading-[1.15] font-semibold text-white">
              A publicação legal da sua instituição, do começo ao comprovante.
            </h2>
            <p className="mt-5 leading-relaxed text-marca-100/85">
              Instituições de ensino da Bahia precisam publicar a relação de concluintes do
              Ensino Médio e da Educação Profissional Técnica no Diário Oficial — e nas
              privadas, o ônus é da própria escola.
            </p>
            <p className="mt-3 text-sm text-marca-200/70">
              Resolução CEE nº 44/2020, Art. 4º e § 1º
            </p>

            {/*
              Aqui havia "3.821 instituições · 333 municípios", números que não
              reproduzem em base nenhuma. A tela de login é pública e roda antes
              de qualquer token, então não há dado vivo para buscar: ou seria
              número fixo — que envelhece e mente —, ou é o processo, que não
              muda. É o processo.
            */}
            <ol className="mt-12 grid grid-cols-3 gap-8 border-t border-white/10 pt-8">
              {[
                { n: '1', t: 'Envie a relação', d: 'nome, curso e ano de conclusão' },
                { n: '2', t: 'Aprove o valor', d: 'calculado pela tabela oficial' },
                { n: '3', t: 'Receba o comprovante', d: 'PDF da página publicada' },
              ].map((i) => (
                <li key={i.n}>
                  <span className="flex size-6 items-center justify-center rounded-full bg-white/10 text-[11px] font-semibold text-white">
                    {i.n}
                  </span>
                  <p className="mt-2 text-sm leading-snug font-medium text-white">{i.t}</p>
                  <p className="mt-1 text-xs text-marca-300/70">{i.d}</p>
                </li>
              ))}
            </ol>
          </div>

          <p className="text-xs text-marca-300/60">
            Empresa Gráfica da Bahia · Casa Civil · Governo do Estado da Bahia
          </p>
        </div>
      </aside>
    </main>
  )
}
